import React from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Page from "../Page";
import DataTable, { DataColumn } from "../DataTable";
import { getStoreIcon } from "../Campaign";
import { GET_MODERATION_ORDERS } from "../../api/graphql/moderation";
import { formatOrderType } from "../../utils/formatters";
import ModerationSettingsForm from "./ModerationSettingsForm";
import ModerationActions from "./ModerationActions";
import { describeModeration, formatMoment } from "./moderationState";
import CountUp from "../../motion/CountUp";
import styles from "./styles.module.scss";

/** "2026-10-15 12:00" → "15.10 12:00"; ratings and reviews have no time. */
const formatStart = (value?: string | null) => {
  if (!value) return "—";
  const date = dayjs(value);
  if (!date.isValid()) return value;
  return date.format(value.includes(":") ? "DD.MM HH:mm" : "DD.MM");
};

/**
 * Orders wait here after they are placed and paid, before they go to the
 * contractor; above them, how they are moderated.
 */
const ModerationPage: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_MODERATION_ORDERS, {
    fetchPolicy: "no-cache",
  });
  const orders: any[] = data?.getModerationOrders || [];
  const waiting = orders.filter((order) => !order.moderation?.approvedAt);

  // What the decision needs comes first, up to the buttons; who placed the
  // order and when are past them.
  const columns: DataColumn<any>[] = [
    {
      title: "ID",
      key: "hashId",
      width: 88,
      fixed: true,
      render: (order) => (
        <Link
          to={`/campaigns/${order.orderType}/${order.id}`}
          className={styles.link}
        >
          {order.hashId}
        </Link>
      ),
      sorter: (a, b) => a.hashId - b.hashId,
    },
    {
      title: "App",
      key: "app",
      width: 240,
      mobile: "full",
      render: (order) => (
        <span className={styles.app}>
          {order.app?.store && getStoreIcon(order.app.store)}
          <span title={order.app?.appId}>{order.app?.title || "—"}</span>
          {order.app?.country && (
            <span className={styles.geo}>
              {order.app.country.toUpperCase()}
            </span>
          )}
        </span>
      ),
      sorter: (a, b) => (a.app?.title || "").localeCompare(b.app?.title || ""),
    },
    {
      title: "Type",
      key: "type",
      width: 112,
      render: (order) => formatOrderType(order.orderType, order.subtype),
    },
    {
      title: "Sell",
      key: "sell",
      width: 96,
      render: (order) => `$${Number(order.orderPrice || 0).toFixed(2)}`,
      sorter: (a, b) => (a.orderPrice || 0) - (b.orderPrice || 0),
    },
    {
      title: "Start",
      key: "start",
      width: 112,
      render: (order) => formatStart(order.initialDate),
      sorter: (a, b) =>
        dayjs(a.initialDate).valueOf() - dayjs(b.initialDate).valueOf(),
    },
    {
      title: "State",
      key: "state",
      width: 260,
      mobile: "full",
      render: (order) => (
        <span className={styles.state}>
          {describeModeration(order.moderation)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 172,
      mobile: "footer",
      render: (order) => (
        <ModerationActions
          orderType={order.orderType}
          hashId={order.hashId}
          approved={Boolean(order.moderation?.approvedAt)}
          onDone={refetch}
        />
      ),
    },
    {
      title: "Email",
      key: "email",
      width: 220,
      mobile: "full",
      render: (order) =>
        order.user ? (
          <Link to={`/user/${order.user.id}`} className={styles.link}>
            {order.user.email}
          </Link>
        ) : (
          "—"
        ),
      sorter: (a, b) =>
        (a.user?.email || "").localeCompare(b.user?.email || ""),
    },
    {
      title: "Placed",
      key: "createdAt",
      width: 112,
      // createdAt comes as epoch milliseconds.
      render: (order) =>
        order.createdAt
          ? formatMoment(dayjs(Number(order.createdAt)).toISOString())
          : "—",
      sorter: (a, b) => Number(a.createdAt) - Number(b.createdAt),
    },
  ];

  return (
    <Page title="Moderation" fitScreen className={styles.page}>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Settings</h2>
        <ModerationSettingsForm />
      </section>
      <section className={`${styles.section} ${styles.queue}`}>
        <h2 className={styles.sectionTitle}>
          In moderation{" "}
          <span className={styles.count}>
            <CountUp to={orders.length} /> · <CountUp to={waiting.length} />{" "}
            waiting for a decision
          </span>
        </h2>
        {error ? (
          <p>The orders in moderation could not be loaded.</p>
        ) : loading ? (
          <p>Loading…</p>
        ) : (
          <DataTable
            id="moderation"
            columns={columns}
            rows={orders}
            rowKey={(order: any) => `${order.orderType}-${order.id}`}
            defaultSort={{ key: "createdAt", direction: "asc" }}
            emptyText="No orders in moderation"
          />
        )}
      </section>
    </Page>
  );
};

export default ModerationPage;
